import { z } from 'zod';
import type { TFunction } from 'i18next';

// Schema factory that accepts translation function
export const createEventSchema = (t: TFunction) =>
  z.object({
    description: z.string().min(3, t('validation.descriptionRequired')),
    location: z.string().optional(),
    startsAt: z.date({
      required_error: t('validation.startDateRequired'),
      invalid_type_error: t('validation.startDateRequired'),
    }),
    endsAt: z.date({
      required_error: t('validation.endDateRequired'),
      invalid_type_error: t('validation.endDateRequired'),
    }),
    attendees: z.array(z.number()).optional(),
    target: z.enum(['FIRM_EVENT', 'NEW_CLIENT', 'CASE_MEETING'], {
      required_error: t('validation.targetRequired'),
    }),
    matterId: z.number().optional(),
    clientId: z.number().optional(),
  }).refine(
    (data) => data.target !== 'CASE_MEETING' || typeof data.matterId === 'number',
    {
      path: ['matterId'],
      message: t('validation.matterRequiredForCaseMeeting'),
    }
  ).refine(
    (data) => data.endsAt >= data.startsAt,
    {
      message: t('validation.endDateAfterStart'),
      path: ['endsAt'],
    }
  );

// Default schema for type inference (uses English fallback)
export const eventSchema = z.object({
  description: z.string().min(3, 'Description is required'),
  location: z.string().optional(),
  startsAt: z.date({
    required_error: 'Start date is required',
    invalid_type_error: 'Invalid start date',
  }),
  endsAt: z.date({
    required_error: 'End date is required',
    invalid_type_error: 'Invalid end date',
  }),
  attendees: z.array(z.number()).optional(),
  target: z.enum(['FIRM_EVENT', 'NEW_CLIENT', 'CASE_MEETING'], {
    required_error: 'Target is required',
  }),
  matterId: z.number().optional(),
  clientId: z.number().optional(),
}).refine(
  (data) => data.target !== 'CASE_MEETING' || typeof data.matterId === 'number',
  {
    path: ['matterId'],
    message: 'Case is required when target is CASE_MEETING',
  }
).refine(
  (data) => data.endsAt >= data.startsAt,
  {
    message: 'End date must be after start date',
    path: ['endsAt'],
  }
);

export type EventFormValues = z.infer<typeof eventSchema>;
